interface RequestOptions extends RequestInit {
    tenantId?: string | null;
    skipTenantHeader?: boolean;
    authToken?: string | null;
}

/**
 * Ritenta una funzione asincrona con un numero massimo di tentativi
 * @param fn - Funzione da riprovare
 * @param maxRetries - Numero massimo di tentativi
 * @param delayMs - Ritardo tra i tentativi (ms)
 * @param retryCondition - Condizione per riprovare (default: ritenta per tutti gli errori)
 */
async function retry<T>(
    fn: () => Promise<T>,
    maxRetries: number = 3,
    delayMs: number = 500,
    retryCondition: (error: any) => boolean = () => true
): Promise<T> {
    let lastError: any;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;

            // Verifica se dobbiamo ritentare
            if (attempt < maxRetries && retryCondition(error)) {
                // retry
                await new Promise(resolve => setTimeout(resolve, delayMs));
                continue;
            }

            break;

        }
    }

    throw lastError;
}

export async function apiFetch(url: string, opts: RequestOptions = {}) {
    const { tenantId, skipTenantHeader, headers, authToken, ...rest } = opts;
    const finalHeaders: Record<string, string> = {
        'Accept': 'application/json',
        ...(headers as any)
    };
    if (tenantId && !skipTenantHeader) finalHeaders['X-Tenant-Id'] = String(tenantId);
    if (authToken) finalHeaders['Authorization'] = `Bearer ${authToken}`;

    // Aggiungi automaticamente l'header Accept-Language se c'è una lingua salvata
    const currentLanguage = localStorage.getItem('menu-language');
    if (currentLanguage && currentLanguage !== 'it') {
        finalHeaders['Accept-Language'] = currentLanguage;
    }

    let targetUrl = url;
    if (typeof url === 'string' && url.startsWith('/api/')) {
        const { getBackendBaseOrigin } = await import('./network');
        const apiBase = getBackendBaseOrigin();
        targetUrl = apiBase + url;
    }

    // dev log removed

    return retry(
        async () => {
            const res = await fetch(targetUrl, { ...rest, headers: finalHeaders });

            const contentType = res.headers.get('content-type') || '';
            if (res.ok) {
                if (contentType.includes('text/html')) {
                    const text = await res.text();
                    // HTML instead of JSON
                    throw new Error('Risposta HTML inattesa');
                }
                if (contentType && !contentType.includes('application/json')) {
                    // wrong content-type
                    throw new Error(`Risposta non valida dal server: ${contentType}`);
                }
                // Sniff: anche se l'header sembra ok, alcuni proxy rispondono HTML
                try {
                    const peekText = await res.clone().text();
                    const head = peekText.trim().slice(0, 32).toLowerCase();
                    if (head.startsWith('<!doctype') || head.startsWith('<html')) {
                        // HTML sniffed
                        throw new Error('Risposta HTML inattesa');
                    }
                } catch (_) { /* ignore */ }
            }

            if (res.status === 401) throw new Error('AUTH_REQUIRED');
            if (res.status === 403) throw new Error('FORBIDDEN');
            if (res.status === 404) throw new Error('NOT_FOUND');

            if (!res.ok) {
                try {
                    const peekClone = res.clone();
                    const text = await peekClone.text();
                    if (text.trim().startsWith('<!DOCTYPE html>') || text.trim().startsWith('<html')) {
                        // HTML instead of JSON
                        throw new Error(`Il server ha restituito HTML invece di JSON (${res.status})`);
                    }
                } catch { }
            }

            return res;
        },
        2,
        1000,
        (error) => {
            return !(error.message === 'AUTH_REQUIRED' ||
                error.message === 'FORBIDDEN' ||
                error.message === 'NOT_FOUND');
        }
    ).catch(error => { throw error; });
}
